/**
 * A captain is a stat modifier with a cost, and two details that are nobody's business.
 *
 * `seed.ts` builds a person you can remember. This file gives that person a reason to matter
 * before the run has started: **one boon, one flaw, both real, both priced against each other**,
 * plus two flavour lines that do nothing at all.
 *
 * ---------------------------------------------------------------------------------------------
 * ### Pricing — every trait is worth the same, and all of them are worth less than a level-up
 *
 * The first draft priced each trait at one rank of the passive that drives the same stat. That
 * looked elegant and enumerated badly: measured against the starter power model in
 * `tests/captain-traits.test.ts`, one rank of Bathyal Hull is worth 4.0 % of composite power and
 * one rank of Lure Cilia is worth 1.0 %, so a `+12 HULL / -15% MAGNET RADIUS` captain was **+3 %
 * up on a captainless run** and the whole 210-pair table spanned 11.6 %. That is a slot machine.
 *
 * So the magnitudes are normalised instead, anchored on the cheapest survivor: **one rank of Lure
 * Cilia, +15 % pickup radius, ≈1.0 % of power.** Everything else is cut to match, which means the
 * loud stats end up well under one rank — `+3% DAMAGE` against Photophore Amplifier's `+8`, `+5
 * HULL` against Bathyal Hull's `+12`. That is the correct shape twice over: the table's internal
 * spread collapses, and a captain is always a *smaller* move than the level-up choices the player
 * actually makes. Nobody should reroll a seed to beat a pick they get every ninety seconds.
 *
 * A captain also draws a **weight** — slight (×0.75), plain (×1.0), marked (×1.5) — and it scales
 * the boon and the flaw by the same factor. A marked captain is louder in both directions and
 * still nominally zero-sum, which is what makes `+23% MAGNET RADIUS / −11% SALVAGE` a character
 * rather than a jackpot.
 *
 * ### Which stats are allowed to be a flaw, and why most are not
 *
 * A flaw that a build cannot feel is free power, and this codebase has three ways for that to
 * happen. All three were measured against the shipped sim, not assumed:
 *
 *  1. **The sim clamps the negative away.** `src/sim/damage.ts:426` reads player armor as
 *     `clamp(p.stats.armor, 0, MITIGATION_CAP)` and `src/sim/damage.ts:239` reads the crit
 *     multiplier as `clamp(crit_mult, CRIT_MULT_BASE, CRIT_MULT_CAP)`. Negative armor and
 *     sub-2.0 crit damage are both discarded on the way in. `src/sim/player.ts:384` guards regen
 *     with `regen_per_s > 0`, so a negative mend is not a bleed, it is nothing.
 *     → `armor_flat` and `regen_hp_per_s` are **boons only**; `crit_damage_pct` is out
 *     altogether, for the separate reason in 4.
 *  2. **A starter cannot read the stat at all.** `maintainOrbiters` (`src/sim/weapons.ts:458`)
 *     never touches `slot.cooldown_until`, never reads `spawnStats.speed`, and pins
 *     `duration_s = -1`. Haste, projectile speed and duration are all exactly zero on the
 *     **Copepod Halo**, and the Halo is one tap away on the loadout screen — so a fire-rate flaw
 *     is a flaw the player can simply decline. → those three stats appear **nowhere** in this
 *     table, in either pool.
 *  3. **A starter reads the stat far more weakly than another.** `area_pct` scales the Halo's 6 u
 *     orbit ring and the Lance's 0.6 u impact radius: measured, 1.99 % of power against 0.45 %, a
 *     4.4× gap no single magnitude serves. → `area_pct` is in **neither** pool.
 *  4. **The stat is priced so low that the line reads as a lie.** `crit_damage_pct` at the 5 %
 *     base crit chance is worth 0.26 % of power for a whole passive rank. A boon nobody can feel
 *     is the same sin as a flaw nobody pays. → `crit_damage_pct` is in **neither** pool.
 *
 * What survives is nine boons and seven flaws, 56 reachable pairs, every one of them felt by all
 * three starters. The enumeration lives in `tests/captain-traits.test.ts` and prints on every run.
 *
 * ### Traits do not consult the temperament, on purpose
 *
 * The temperament is a fact about the mouth; the trait is a fact about the hands and the wiring.
 * Two reasons for keeping them apart, one of them mechanical:
 *
 *  - **Balance.** Temperament comes off the same run seed as the traits. Constraining the flaw
 *    pool by temperament would make temperament a mechanical reroll target and would split one
 *    balance surface into seven, each of which would then have to be enumerated separately.
 *  - **Writing.** A nervy captain with nervy stats says the same thing three times. The mismatch
 *    is where a person lives: `worrier` + `+3% CRIT CHANCE` is somebody who checks everything and
 *    is, annoyingly, right.
 *
 * The **flavour lines** carry the temperament instead, and they carry it in the shape the ask
 * asked for: one line from the temperament's own pool, one from a shared pool that reads
 * differently depending on who is holding it. `HUMS OFF-KEY` is a joke about a `liturgist` and a
 * different joke about a `firebrand`, so it lives in the shared pool where it can be both.
 *
 * ### Determinism
 *
 * Own sub-stream: `captainSubSeed(runSeed, 'traits')`, disjoint from `identity` and `face`, so
 * adding this gene moved no name and no face — `tests/captain-traits.test.ts` pins 4 000 seeds
 * against goldens captured before it existed. No `Math.random`, no clock, no DOM, no storage.
 * The table itself is hashed into `content_hash` by `src/core/content.ts`, so tuning a magnitude
 * invalidates the replays it changed the meaning of.
 */

import { CAPTAIN_TRAITS } from '../core/content';
import type { RngStream } from '../core/rng';
import type { TemperamentId } from './seed';

// ---------------------------------------------------------------------------
// Stat keys — the ADD buckets `src/sim/build.ts` sums before the §4.2 caps
// ---------------------------------------------------------------------------

/**
 * Every key here is a `stat_effects[0].stat` value that already exists in `passives.json` and
 * already has a `case` arm in `recomputeStats`. A captain opens no new stat.
 */
export const CAPTAIN_STAT_KEYS = [
  'damage_pct',
  'move_speed_pct',
  'max_hp_flat',
  'armor_flat',
  'xp_gain_pct',
  'pickup_radius_pct',
  'crit_chance_pct',
  'regen_hp_per_s',
  'dash_cooldown_s',
] as const;
export type CaptainStatKey = (typeof CAPTAIN_STAT_KEYS)[number];

/** The vector `src/sim/build.ts` adds. One field per key, all zero when there is no captain. */
export type CaptainStatAdds = Record<CaptainStatKey, number>;

// ---------------------------------------------------------------------------
// Table shape
// ---------------------------------------------------------------------------

interface TraitRow {
  id: string;
  stat: string;
  label: string;
  unit: string;
  precision: number;
  magnitude: number;
  display_magnitude: number;
  note: string;
}

interface WeightRow {
  id: string;
  label: string;
  scale: number;
  odds: number;
}

/** How loud this captain is in both directions at once. Scales boon and flaw identically. */
export interface TraitWeight {
  id: string;
  label: string;
  scale: number;
}

/**
 * One resolved trait, already scaled by the captain's weight.
 *
 * `sign` is a **valence**, not an arithmetic sign: `flaw.slow-to-settle` carries `sign: -1` and
 * `magnitude: +0.25`, because adding seconds to the dash cooldown is a bad thing that is a
 * positive number. The card colours by `sign`; the sim adds `magnitude`; `display` already
 * agrees with `sign`.
 */
export interface CaptainTrait {
  /** `boon.magnetic-hands`. Stable, referenced by tests and by the loadout card. */
  id: string;
  /** +1 boon (green), −1 flaw (red). */
  sign: 1 | -1;
  /** Which ADD bucket this lands in. */
  stat: CaptainStatKey;
  /** Signed in the stat's own unit, scaled and rounded. What the sim adds. */
  magnitude: number;
  /** `MAGNET RADIUS` — the stat, as the crew says it. */
  label: string;
  /** `+15% MAGNET RADIUS`. Leading sign always matches `sign`. */
  display: string;
  /** One line of prose about the person, not the number. */
  note: string;
}

/** The whole trait block for one captain. This is what the loadout card renders. */
export interface CaptainTraits {
  boon: CaptainTrait;
  flaw: CaptainTrait;
  /** slight / plain / marked. Both traits are scaled by `weight.scale`. */
  weight: TraitWeight;
  /**
   * Two lines with no mechanical effect. `[0]` comes from the temperament's own pool and
   * confirms who they are; `[1]` comes from the shared pool and means something different
   * depending on who drew it.
   */
  flavour: readonly [string, string];
}

// ---------------------------------------------------------------------------
// Table, read once at module load
// ---------------------------------------------------------------------------

const TABLE = CAPTAIN_TRAITS;

export const TRAIT_WEIGHTS: readonly TraitWeight[] = Object.freeze(
  (TABLE.pricing.weights as WeightRow[]).map((w) => Object.freeze({ id: w.id, label: w.label, scale: w.scale }))
);
const WEIGHT_ODDS: readonly number[] = Object.freeze((TABLE.pricing.weights as WeightRow[]).map((w) => w.odds));

const BOON_ROWS = TABLE.boons as TraitRow[];
const FLAW_ROWS = TABLE.flaws as TraitRow[];

/** Trait IDs, in table order. The gates read these; nothing at runtime does. */
export const BOON_IDS: readonly string[] = Object.freeze(BOON_ROWS.map((r) => r.id));
export const FLAW_IDS: readonly string[] = Object.freeze(FLAW_ROWS.map((r) => r.id));

const FLAVOUR = TABLE.flavour as Record<string, string[]>;
export const SHARED_FLAVOUR: readonly string[] = Object.freeze(FLAVOUR.shared.slice());

/**
 * Flaw indices reachable for each boon stat, built once.
 *
 * A boon and a flaw on the same stat cancel to a rounding error and print two lines that argue
 * with each other, so the same-stat pairing is removed from the pool rather than rejected after
 * the draw — one `next_int` per captain, no rejection loop, no allocation at draw time.
 */
const FLAW_POOL_BY_BOON: readonly (readonly number[])[] = Object.freeze(
  BOON_ROWS.map((b) =>
    Object.freeze(FLAW_ROWS.map((f, i) => (f.stat === b.stat ? -1 : i)).filter((i) => i >= 0))
  )
);

// ---------------------------------------------------------------------------
// Scaling
// ---------------------------------------------------------------------------

/**
 * Round half away from zero, not `Math.round`'s half-toward-`+∞`. Without this the dash pair
 * printed `+13% DASH RECOVERY` for the boon and `-12%` for the flaw off the same 12.5, and two
 * captains carrying the same trait at the same weight have to show the same number.
 */
function roundTo(v: number, decimals: number): number {
  const p = Math.pow(10, decimals);
  return (v < 0 ? -Math.round(-v * p) : Math.round(v * p)) / p;
}

/**
 * Sim magnitude at a given weight.
 *
 * Rounded to the row's display `precision`, so the number the card prints is the number the sim
 * adds — with one documented exception. `dash_cooldown_s` is stated in seconds and displayed as a
 * percentage of the base cooldown, so its two magnitudes cannot share a precision; the seconds
 * are kept to two decimals, which is finer than the 60 Hz tick can resolve.
 */
function scaledMagnitude(row: TraitRow, scale: number): number {
  const decimals = row.stat === 'dash_cooldown_s' ? 2 : row.precision;
  return roundTo(row.magnitude * scale, decimals);
}

function scaledDisplay(row: TraitRow, scale: number): number {
  return roundTo(row.display_magnitude * scale, row.precision);
}

/** `+15% MAGNET RADIUS`. ASCII hyphen, not U+2212 — Silkscreen has no minus-sign glyph. */
function displayString(value: number, unit: string, label: string, precision: number): string {
  const head = value < 0 ? '-' : '+';
  const body = Math.abs(value).toFixed(precision);
  return unit === 'percent' ? `${head}${body}% ${label}` : `${head}${body} ${label}`;
}

function resolve(row: TraitRow, sign: 1 | -1, scale: number): CaptainTrait {
  const magnitude = scaledMagnitude(row, scale);
  const shown = scaledDisplay(row, scale);
  return {
    id: row.id,
    sign,
    stat: row.stat as CaptainStatKey,
    magnitude,
    label: row.label,
    display: displayString(shown, row.unit, row.label, row.precision),
    note: row.note,
  };
}

// ---------------------------------------------------------------------------
// The draw
// ---------------------------------------------------------------------------

function pickWeighted(rng: RngStream, weights: readonly number[]): number {
  let total = 0;
  for (let i = 0; i < weights.length; i++) total += weights[i];
  let r = rng.next() * total;
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i];
    if (r < 0) return i;
  }
  return weights.length - 1;
}

/**
 * Five draws off the `captain.traits` sub-stream, in this order: weight, boon, flaw, own
 * flavour line, shared flavour line. Nothing here reads or perturbs `identity` or `face`.
 */
export function drawTraits(rng: RngStream, temperament: TemperamentId): CaptainTraits {
  const weight = TRAIT_WEIGHTS[pickWeighted(rng, WEIGHT_ODDS)];

  const boonIndex = rng.next_int(BOON_ROWS.length);
  const boon = resolve(BOON_ROWS[boonIndex], 1, weight.scale);

  const pool = FLAW_POOL_BY_BOON[boonIndex];
  const flaw = resolve(FLAW_ROWS[pool[rng.next_int(pool.length)]], -1, weight.scale);

  const own = FLAVOUR[temperament];
  const first = own[rng.next_int(own.length)];
  const second = FLAVOUR.shared[rng.next_int(FLAVOUR.shared.length)];

  return { boon, flaw, weight, flavour: [first, second] };
}

// ---------------------------------------------------------------------------
// The sim hook
// ---------------------------------------------------------------------------

function zeroed(): CaptainStatAdds {
  const out = {} as CaptainStatAdds;
  for (let i = 0; i < CAPTAIN_STAT_KEYS.length; i++) out[CAPTAIN_STAT_KEYS[i]] = 0;
  return out;
}

/**
 * The live vector `src/sim/build.ts` folds into its ADD buckets.
 *
 * Module scope, mutated in place, never reallocated — the same discipline `buildExtras` uses, and
 * for the same reason: `recomputeStats` reads it on every build change and §14.3 forbids
 * allocating there. All zeroes until `setCaptainTraits` is called, so a headless harness that
 * never builds a captain gets the untouched baseline.
 */
export const CAPTAIN_STAT_ADDS: CaptainStatAdds = zeroed();

/**
 * Publish a captain's traits to the sim, or clear them with `null`.
 *
 * Call once per run, before the first `recomputeStats`, and again with `null` on run reset. Pure
 * in its argument: same captain in, same vector out, so a replay of the same seed lands on the
 * same numbers.
 */
export function setCaptainTraits(traits: CaptainTraits | null): void {
  for (let i = 0; i < CAPTAIN_STAT_KEYS.length; i++) CAPTAIN_STAT_ADDS[CAPTAIN_STAT_KEYS[i]] = 0;
  if (traits === null) return;
  CAPTAIN_STAT_ADDS[traits.boon.stat] += traits.boon.magnitude;
  CAPTAIN_STAT_ADDS[traits.flaw.stat] += traits.flaw.magnitude;
}

/** The same fold as `setCaptainTraits`, into a fresh object. For gates and for the balance table. */
export function statAddsOf(traits: CaptainTraits): CaptainStatAdds {
  const out = zeroed();
  out[traits.boon.stat] += traits.boon.magnitude;
  out[traits.flaw.stat] += traits.flaw.magnitude;
  return out;
}
